/**
 * KFO Color Combinations
 * Handles yarn filter, search, accordion, infinite scroll, and lightbox
 */

class KFOCombinations {
  constructor() {
    this.container = document.getElementById('kfo-combinations');
    if (!this.container) return;

    this.grid = document.getElementById('kfo-combinations-grid');
    this.searchInput = this.container.querySelector('.kfo-search-input');
    this.filterTabs = this.container.querySelectorAll('.kfo-filter-tab');
    this.loadingIndicator = document.getElementById('kfo-loading');
    this.remainingTemplate = document.getElementById('kfo-remaining-items');
    this.loadingSkeleton = document.getElementById('kfo-loading-skeleton');

    this.currentlyOpenCard = null;
    this.currentFilter = 'all';
    this.searchTerm = '';
    this.remainingItems = [];
    this.isLoading = false;
    this.allLoaded = false;

    // For reverse lookup (loaded from pre-computed JSON)
    this.reverseIndex = null;
    this.showingReverse = false;

    // Load translations
    this.translations = this.loadTranslations();

    this.init();
  }

  init() {
    this.setupFilterTabs();
    this.setupSearch();
    this.setupAccordions();
    this.setupInfiniteScroll();
    this.setupLightbox();
    this.setupAddToCart();
    this.loadRemainingItems();
    // Load reverse index from pre-computed JSON (no DOM scanning needed)
    this.loadReverseIndexFromJSON();
    // Hide skeleton, show grid
    this.showContent();
  }

  /**
   * Load translations from JSON config
   */
  loadTranslations() {
    const defaults = {
      viewProduct: 'Produkt ansehen',
      showCombinations: 'Kombinationen ansehen',
      combinationsHeading: 'Kombinationen:',
      addToCart: 'In den Warenkorb',
      adding: 'Wird hinzugefügt...',
      added: 'Hinzugefügt!',
      error: 'Fehler',
      zoom: 'Vergrößern',
      noResults: 'Keine Farben gefunden. Versuche es mit einem anderen Suchbegriff.'
    };

    const script = document.getElementById('kfo-translations');
    if (!script) return defaults;

    try {
      return { ...defaults, ...JSON.parse(script.textContent) };
    } catch (e) {
      console.warn('KFO: Failed to parse translations');
      return defaults;
    }
  }

  /**
   * Hide loading skeleton and show content
   */
  showContent() {
    if (this.loadingSkeleton) {
      this.loadingSkeleton.classList.add('hidden');
    }
    if (this.grid) {
      this.grid.classList.add('kfo-ready');
    }
  }

  /**
   * Yarn Type Filter Tabs
   */
  setupFilterTabs() {
    this.filterTabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const filter = tab.dataset.yarnFilter;
        this.setFilter(filter);

        // Update active tab
        this.filterTabs.forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
      });
    });
  }

  setFilter(filter) {
    this.currentFilter = filter;

    // Load all remaining items if not already loaded
    // This ensures filtering works with the complete dataset
    if (!this.allLoaded && this.remainingItems.length > 0) {
      this.loadAllRemainingItems();
    }

    // If filtering to Soft Silk Mohair or Compatible Cashmere, show reverse view
    // Merino and Heavy Merino show forward view (the original entries)
    if (filter === 'Soft Silk Mohair' || filter === 'Compatible Cashmere') {
      this.showReverseView(filter);
    } else {
      // Merino or Heavy Merino shows forward view
      this.showForwardView();
    }

    this.applyFilters();
  }

  /**
   * Search Functionality
   */
  setupSearch() {
    if (!this.searchInput) return;

    this.searchInput.addEventListener('input', this.debounce(() => {
      this.searchTerm = this.searchInput.value.toLowerCase().trim();
      this.applyFilters();
    }, 300));
  }

  /**
   * Apply Both Filters (Yarn Type + Search)
   */
  applyFilters() {
    const cards = this.grid.querySelectorAll('.kfo-color-card');
    let visibleCount = 0;

    cards.forEach(card => {
      const colorName = card.dataset.colorName || '';
      const yarnType = card.dataset.yarnType || '';

      // Check yarn type filter
      const matchesFilter = this.currentFilter === 'all' || yarnType === this.currentFilter;

      // Check search term
      const matchesSearch = !this.searchTerm || colorName.includes(this.searchTerm);

      // Show/hide card
      if (matchesFilter && matchesSearch) {
        card.classList.remove('hidden');
        visibleCount++;
      } else {
        card.classList.add('hidden');

        // Close accordion if it was open
        if (card.classList.contains('is-expanded')) {
          this.closeAccordion(card);
        }
      }
    });

    // Show/hide empty state
    this.toggleEmptyState(visibleCount === 0);
  }

  toggleEmptyState(show) {
    let emptyState = this.grid.querySelector('.kfo-empty-state');

    if (show && !emptyState) {
      emptyState = document.createElement('div');
      emptyState.className = 'kfo-empty-state';
      emptyState.innerHTML = `<p>${this.translations.noResults}</p>`;
      this.grid.appendChild(emptyState);
    } else if (!show && emptyState) {
      emptyState.remove();
    }
  }

  /**
   * Accordion Functionality
   */
  setupAccordions() {
    this.grid.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-accordion-trigger]');
      if (!trigger) return;

      const card = trigger.closest('.kfo-color-card');
      if (!card) return;

      this.toggleAccordion(card);
    });
  }

  toggleAccordion(card) {
    const isExpanded = card.classList.contains('is-expanded');

    if (isExpanded) {
      this.closeAccordion(card);
    } else {
      // Close currently open card
      if (this.currentlyOpenCard && this.currentlyOpenCard !== card) {
        this.closeAccordion(this.currentlyOpenCard);
      }
      this.openAccordion(card);
    }
  }

  openAccordion(card) {
    card.classList.add('is-expanded');
    this.currentlyOpenCard = card;

    // Lazy load images in this accordion
    this.lazyLoadImages(card);
  }

  closeAccordion(card) {
    card.classList.remove('is-expanded');
    if (this.currentlyOpenCard === card) {
      this.currentlyOpenCard = null;
    }
  }

  /**
   * Lazy Load Images
   */
  lazyLoadImages(container) {
    const images = container.querySelectorAll('img.lazyload[data-src]');

    images.forEach(img => {
      if (!img.src || img.src === '') {
        img.src = img.dataset.src;
        img.classList.remove('lazyload');
        img.classList.add('lazyloaded');
      }
    });
  }

  /**
   * Infinite Scroll
   */
  setupInfiniteScroll() {
    // Create a sentinel element at the bottom of the grid
    this.scrollSentinel = document.createElement('div');
    this.scrollSentinel.className = 'kfo-scroll-sentinel';
    this.scrollSentinel.style.height = '10px';
    this.scrollSentinel.style.width = '100%';
    this.grid.appendChild(this.scrollSentinel);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.isLoading && !this.allLoaded) {
            this.loadMoreItems();
          }
        });
      },
      { rootMargin: '400px', threshold: 0 }
    );

    observer.observe(this.scrollSentinel);

    // Also check immediately in case sentinel is already visible
    setTimeout(() => {
      const rect = this.scrollSentinel.getBoundingClientRect();
      if (rect.top < window.innerHeight + 400 && !this.allLoaded && !this.isLoading) {
        this.loadMoreItems();
      }
    }, 100);
  }

  loadRemainingItems() {
    if (!this.remainingTemplate) {
      this.allLoaded = true;
      return;
    }

    // Use .content for <template> elements - .innerHTML doesn't work reliably
    const templateContent = this.remainingTemplate.content;
    this.remainingItems = Array.from(templateContent.querySelectorAll('.kfo-color-card'));

    if (this.remainingItems.length === 0) {
      this.allLoaded = true;
    }
  }

  loadMoreItems() {
    if (this.allLoaded || this.isLoading) return;

    this.isLoading = true;
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = 'block';
    }

    setTimeout(() => {
      const itemsToLoad = this.remainingItems.splice(0, 15);

      itemsToLoad.forEach((item, index) => {
        // Stagger fade-in animation for smooth loading effect
        item.classList.add('kfo-fade-in');
        item.style.animationDelay = `${index * 50}ms`;
        this.grid.appendChild(item);
      });

      // Apply current filters to new items
      this.applyFilters();

      if (this.remainingItems.length === 0) {
        this.allLoaded = true;
      }

      this.isLoading = false;
      if (this.loadingIndicator) {
        this.loadingIndicator.style.display = 'none';
      }
    }, 300);
  }

  loadAllRemainingItems() {
    if (this.allLoaded || this.remainingItems.length === 0) return;

    // Load all remaining items at once (used when filtering)
    this.remainingItems.forEach(item => {
      this.grid.appendChild(item);
    });

    this.remainingItems = [];
    this.allLoaded = true;
  }

  /**
   * Custom Lightbox
   */
  setupLightbox() {
    // Create lightbox HTML
    this.createLightboxHTML();

    // Listen for trigger clicks
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-lightbox-trigger]');
      if (!trigger) return;

      e.preventDefault();

      // Get all images in the same card
      const card = trigger.closest('.kfo-color-card');
      const allTriggers = Array.from(card.querySelectorAll('[data-lightbox-trigger]'));

      const items = allTriggers.map(item => ({
        src: item.dataset.pswpSrc,
        alt: item.querySelector('img')?.alt || ''
      }));

      const index = allTriggers.indexOf(trigger);
      this.openLightbox(items, index);
    });
  }

  createLightboxHTML() {
    const lightbox = document.createElement('div');
    lightbox.className = 'kfo-lightbox-overlay';
    lightbox.innerHTML = `
      <button class="kfo-lightbox-close" aria-label="Close">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
      <button class="kfo-lightbox-nav kfo-lightbox-prev" aria-label="Previous">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <button class="kfo-lightbox-nav kfo-lightbox-next" aria-label="Next">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <div class="kfo-lightbox-content">
        <img class="kfo-lightbox-image" src="" alt="">
      </div>
    `;
    document.body.appendChild(lightbox);

    this.lightbox = lightbox;
    this.lightboxImage = lightbox.querySelector('.kfo-lightbox-image');
    this.lightboxPrev = lightbox.querySelector('.kfo-lightbox-prev');
    this.lightboxNext = lightbox.querySelector('.kfo-lightbox-next');
    this.lightboxClose = lightbox.querySelector('.kfo-lightbox-close');

    // Event listeners
    this.lightboxClose.addEventListener('click', () => this.closeLightbox());
    this.lightboxPrev.addEventListener('click', () => this.navigateLightbox(-1));
    this.lightboxNext.addEventListener('click', () => this.navigateLightbox(1));
    this.lightbox.addEventListener('click', (e) => {
      if (e.target === this.lightbox) this.closeLightbox();
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!this.lightbox.classList.contains('active')) return;

      if (e.key === 'Escape') this.closeLightbox();
      if (e.key === 'ArrowLeft') this.navigateLightbox(-1);
      if (e.key === 'ArrowRight') this.navigateLightbox(1);
    });
  }

  openLightbox(items, startIndex = 0) {
    this.lightboxItems = items;
    this.lightboxIndex = startIndex;
    this.showLightboxImage();
    this.lightbox.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  closeLightbox() {
    this.lightbox.classList.remove('active');
    document.body.style.overflow = '';
  }

  navigateLightbox(direction) {
    this.lightboxIndex += direction;

    if (this.lightboxIndex < 0) {
      this.lightboxIndex = this.lightboxItems.length - 1;
    } else if (this.lightboxIndex >= this.lightboxItems.length) {
      this.lightboxIndex = 0;
    }

    this.showLightboxImage();
  }

  showLightboxImage() {
    const item = this.lightboxItems[this.lightboxIndex];

    // Fade out
    this.lightboxImage.style.opacity = '0';

    setTimeout(() => {
      this.lightboxImage.src = item.src;
      this.lightboxImage.alt = item.alt;

      // Fade in
      this.lightboxImage.style.opacity = '1';
    }, 150);

    // Update navigation buttons
    this.lightboxPrev.disabled = false;
    this.lightboxNext.disabled = false;

    if (this.lightboxItems.length === 1) {
      this.lightboxPrev.disabled = true;
      this.lightboxNext.disabled = true;
    }
  }

  /**
   * Load Reverse Index from Pre-computed JSON
   * No DOM scanning needed - data is computed server-side in Liquid
   */
  loadReverseIndexFromJSON() {
    const jsonScript = document.getElementById('kfo-reverse-index');
    if (!jsonScript) {
      console.warn('KFO: Reverse index JSON not found');
      this.reverseIndex = { 'Soft Silk Mohair': {}, 'Compatible Cashmere': {} };
      return;
    }

    try {
      this.reverseIndex = JSON.parse(jsonScript.textContent);
    } catch (e) {
      console.error('KFO: Failed to parse reverse index JSON', e);
      this.reverseIndex = { 'Soft Silk Mohair': {}, 'Compatible Cashmere': {} };
    }
  }

  /**
   * Show Reverse View (for Soft Silk Mohair/Compatible Cashmere filters)
   */
  showReverseView(yarnType) {
    if (!this.reverseIndex || !this.reverseIndex[yarnType]) return;

    // Hide all original cards in the grid
    const originalCards = this.grid.querySelectorAll('.kfo-color-card:not(.kfo-reverse-card)');
    originalCards.forEach(card => card.classList.add('hidden'));

    // Remove any existing reverse cards
    const existingReverse = this.grid.querySelectorAll('.kfo-reverse-card');
    existingReverse.forEach(card => card.remove());

    // Create reverse cards from JSON data
    const reverseData = this.reverseIndex[yarnType];
    const sortedColors = Object.keys(reverseData).sort();

    sortedColors.forEach(colorKey => {
      const data = reverseData[colorKey];
      const reverseCard = this.createReverseCard(yarnType, data);
      this.grid.appendChild(reverseCard);
    });

    this.showingReverse = true;
  }

  /**
   * Show Forward View (for Merino/Heavy Merino filters)
   */
  showForwardView() {
    // Remove reverse cards
    const reverseCards = this.grid.querySelectorAll('.kfo-reverse-card');
    reverseCards.forEach(card => card.remove());

    // Show original cards
    const originalCards = this.grid.querySelectorAll('.kfo-color-card');
    originalCards.forEach(card => card.classList.remove('hidden'));

    this.showingReverse = false;
  }

  /**
   * Create Reverse Card HTML from JSON data
   */
  createReverseCard(yarnType, data) {
    const card = document.createElement('div');
    card.className = 'kfo-color-card kfo-reverse-card';
    card.dataset.colorName = data.colorName.toLowerCase();
    card.dataset.yarnType = yarnType;

    // Use variant image as thumbnail (the actual SSH/CC product image)
    const thumbnailSrc = data.variantImage || '';
    const baseProductLink = data.productUrl || '#';
    const baseVariantId = data.variantId || '';

    // Build match list from JSON data
    const matchesHTML = data.matches.map(match => {
      const matchYarnType = match.yarnType || 'Merino';
      const imageSrc = match.pairingImage || '';
      const imageSrcLarge = match.pairingImageLarge || imageSrc;
      const colorName = match.colorName || '';
      const viewProductLink = match.productUrl || '#';
      const variantId = match.variantId || '';

      // Generate add-to-cart button (always show since we have variant ID)
      let addToCartHTML = '';
      if (variantId) {
        addToCartHTML = `
          <button class="kfo-add-to-cart-btn" data-variant-id="${variantId}">
            <svg width="16" height="16" viewBox="0 0 24 24" stroke="currentColor" fill="none">
              <path stroke-linecap="round" stroke-miterlimit="10" d="M7.70023 9.14V6.36C7.70023 3.81 9.76023 1.75 12.3002 1.75C14.8402 1.75 16.9102 3.81 16.9102 6.36V9.14M21.8502 19.59L21.1902 9.15C21.1102 7.89 20.0502 6.91 18.7702 6.91H5.83023C4.55023 6.91 3.49023 7.89 3.41023 9.15L2.75023 19.59C2.66023 20.96 3.77023 22.13 5.17023 22.13H19.4402C20.8402 22.13 21.9402 20.97 21.8602 19.59H21.8502Z"></path>
            </svg>
            ${this.translations.addToCart}
          </button>
        `;
      }

      return `
        <div class="kfo-match-item">
          <div class="kfo-match-image-wrapper" data-lightbox-trigger data-pswp-src="${imageSrcLarge}">
            <img src="${imageSrc}" alt="${colorName}" class="kfo-match-image">
            <div class="kfo-image-overlay">
              <svg class="kfo-zoom-icon" width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.35-4.35M11 8v6M8 11h6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span class="kfo-overlay-text">${this.translations.zoom}</span>
            </div>
          </div>
          <div class="kfo-match-info">
            <p class="kfo-match-label">${matchYarnType}</p>
            <p class="kfo-match-name">${colorName}</p>
            <a href="${viewProductLink}" class="kfo-view-product-link">
              ${this.translations.viewProduct} →
            </a>
            ${addToCartHTML}
          </div>
        </div>
      `;
    }).join('');

    // Generate add-to-cart button for the base SSH/CC
    let baseAddToCartHTML = '';
    if (baseVariantId) {
      baseAddToCartHTML = `
        <button class="kfo-add-to-cart-btn kfo-header-cart-btn" data-variant-id="${baseVariantId}">
          ${this.translations.addToCart}
        </button>
      `;
    }

    card.innerHTML = `
      <div class="kfo-card-header" data-accordion-trigger>
        <div class="kfo-card-title-wrapper">
          <div class="kfo-title-content">
            ${thumbnailSrc ? `
              <div class="kfo-card-thumbnail">
                <img src="${thumbnailSrc}" alt="${data.colorName}" loading="lazy">
              </div>
            ` : ''}
            <div class="kfo-title-text">
              <span class="kfo-yarn-badge">${yarnType}</span>
              <h3 class="kfo-card-title">${data.colorName}</h3>
              <a href="${baseProductLink}" class="kfo-base-product-link">
                ${this.translations.viewProduct} →
              </a>
              <p class="kfo-card-subtitle">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                ${this.translations.showCombinations}
              </p>
            </div>
          </div>
          <svg class="kfo-accordion-icon" width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path d="M19 9l-7 7-7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </div>
      <div class="kfo-card-content" data-accordion-content>
        <div class="kfo-matches-group">
          <h4 class="kfo-matches-heading">${this.translations.combinationsHeading}</h4>
          <div class="kfo-matches-grid">
            ${matchesHTML}
          </div>
        </div>
      </div>
    `;

    return card;
  }

  /**
   * Add to Cart Functionality
   */
  setupAddToCart() {
    // Use event delegation to handle both static and dynamic buttons
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('.kfo-add-to-cart-btn');
      if (!btn) return;

      e.preventDefault();

      const variantId = btn.dataset.variantId;
      if (!variantId || btn.classList.contains('adding')) return;

      await this.addToCart(btn, variantId);
    });
  }

  async addToCart(btn, variantId) {
    // Show loading state
    const originalHTML = btn.innerHTML;
    btn.classList.add('adding');
    btn.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="animation: spin 0.8s linear infinite;">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-dasharray="60" stroke-dashoffset="30" fill="none"/>
      </svg>
      ${this.translations.adding}
    `;

    try {
      // Use Shopify AJAX Cart API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const data = await response.json();

      // Show success state
      btn.classList.remove('adding');
      btn.classList.add('added');
      btn.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        ${this.translations.added}
      `;

      // Trigger cart update event (for themes with cart drawer)
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }));

      // Reset button after 2 seconds
      setTimeout(() => {
        btn.classList.remove('added');
        btn.innerHTML = originalHTML;
      }, 2000);

    } catch (error) {
      console.error('Error adding to cart:', error);

      // Show error state
      btn.classList.remove('adding');
      btn.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        ${this.translations.error}
      `;

      // Reset button after 2 seconds
      setTimeout(() => {
        btn.innerHTML = originalHTML;
      }, 2000);
    }
  }

  /**
   * Utility: Debounce
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new KFOCombinations();
  });
} else {
  new KFOCombinations();
}
